import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { assertSectionHeaderState } from "../lib/sectionCardState";

/**
 * AccordionCard — the kit-styled section header + fold, for the flag-on leg
 * only. Deliberately NOT a change to `identityForm.jsx`'s `SectionCard`:
 * that component is shared with Identity & Vision (out of scope, #852's own
 * "explicitly out of scope" list), and every other page that renders one.
 *
 * Same open/close contract as `SectionCard` (`aria-expanded`/`aria-controls`,
 * `toggleTestid`, body unmounted while folded, `${toggleTestid}-count` /
 * `-summary` on the badge, and the same "exactly one of count or summary"
 * guard) — only the visual shell differs, per ReflectVoice.dc.html/
 * Reflect.dc.html's own drawn header row (34px icon well, 15/700 title,
 * 12px muted description, a grey — not navy-tinted — count/summary pill).
 */
export default function AccordionCard({ icon: Icon, title, desc, count = null, summary = null, defaultOpen = false, toggleTestid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  assertSectionHeaderState({ title, count, summary });

  return (
    <div
      style={{
        display: "flex", flexDirection: "column", gap: 12, padding: "4px 14px 14px",
        background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={toggleTestid}
        className="w-full text-left flex items-center min-w-0"
        style={{ gap: 10, minHeight: 56, background: "none", border: "none", padding: 0, cursor: "pointer" }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 34, height: 34, flexShrink: 0, borderRadius: 10, background: "var(--nw-navy-tint)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon size={16} color="var(--nw-navy)" />
        </span>
        <span className="flex flex-col min-w-0" style={{ gap: 1, flexGrow: 1 }}>
          <span className="nw-body-strong" style={{ display: "block" }}>{title}</span>
          {desc && <span className="nw-help" style={{ display: "block" }}>{desc}</span>}
        </span>
        {count != null && (
          <span
            data-testid={`${toggleTestid}-count`}
            style={{
              padding: "2px 8px", borderRadius: "var(--nw-radius-pill)", background: "var(--nw-line-faint)",
              fontSize: 12, fontWeight: 700, color: "var(--nw-ink-lede)", flexShrink: 0,
            }}
          >
            {count}
            <span className="sr-only"> {count === 1 ? "entry" : "entries"}</span>
          </span>
        )}
        {summary != null && (
          <span
            data-testid={`${toggleTestid}-summary`}
            style={{
              padding: "2px 8px", borderRadius: "var(--nw-radius-pill)", background: "var(--nw-line-faint)",
              fontSize: 12, fontWeight: 700, color: "var(--nw-ink-lede)", flexShrink: 0,
            }}
          >
            {summary}
          </span>
        )}
        <ChevronDown
          size={18}
          aria-hidden="true"
          style={{ color: "var(--nw-ink-muted)", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        />
      </button>
      <div id={panelId} style={open ? { display: "flex", flexDirection: "column", gap: 12 } : { display: "none" }}>
        {open && children}
      </div>
    </div>
  );
}
